import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DiscoveryIndicator,
  Icon,
  Image,
  Text,
  Video,
} from "@salestrak/ui";
import {
  ANNOUNCEMENT_TYPE_LABELS,
  announcementDate,
  isEarlyAccess,
  type Announcement,
} from "@salestrak/announcements";
import { MarkdownContent } from "#app/components/navigation/markdown-content";
import { ANNOUNCEMENT_CONTENT } from "#app/components/navigation/whats-new-content";
import { APP_VERSION } from "#app/lib/app-version";
import { CHANGELOG_URL } from "#app/lib/marketing-urls";
import { formatDateUtc } from "#app/lib/utils/format-date";

// Badge variant per entry state: "new" reads informational, shipped work
// reads as success, early access as the odd-one-out outline.
type BadgeVariant = "info" | "success" | "outline";

// Early-access entries label as such; everything else labels by type (the
// shared catalog label, so in-app and the marketing digest agree).
function entryBadge(entry: Announcement): {
  label: string;
  variant: BadgeVariant;
} {
  if (isEarlyAccess(entry)) {
    return { label: "Early access", variant: "outline" };
  }
  return {
    label: ANNOUNCEMENT_TYPE_LABELS[entry.type],
    variant: entry.type === "new" ? "info" : "success",
  };
}

/**
 * The curated What's-New feed: one flush row per announcement entry visible
 * to this viewer, newest first — badge + title + date, markdown body, then
 * optional media, app-side rich content (see `ANNOUNCEMENT_CONTENT`), and
 * CTA. Entries in `unseenKeys` carry the standard discovery dot on their
 * title — the caller passes the live set and marks everything seen when the
 * dialog closes, so the dots stay lit the whole time it is open.
 */
export function WhatsNewDialog({
  open,
  onOpenChange,
  entries,
  unseenKeys,
  onNavigate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: Announcement[];
  unseenKeys: ReadonlySet<string>;
  onNavigate: (route: string) => void;
}) {
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogPopup>
        <DialogHeader>
          <Box align="center" direction="row" gap="sm">
            <Icon color="primary" name="gift" size="lg" />
            <DialogTitle>What's new</DialogTitle>
          </Box>
        </DialogHeader>
        <DialogPanel>
          {entries.length === 0 ? (
            <Box align="center" paddingY="xl">
              <Text variant="muted">
                Nothing new right now — check back soon.
              </Text>
            </Box>
          ) : (
            <Box direction="column" gap="lg">
              {entries.map((entry, index) => (
                <AnnouncementRow
                  key={`${entry.key}-${index}`}
                  entry={entry}
                  onNavigate={onNavigate}
                  unseen={unseenKeys.has(entry.key)}
                />
              ))}
            </Box>
          )}
        </DialogPanel>
        {/* Separated by a rule, not a fill — the dialog is one surface. */}
        <Box
          align="center"
          border="top"
          direction="row"
          gap="xs"
          paddingX="lg"
          paddingY="sm"
        >
          <Text size="sm" variant="muted">
            SalesTrak
          </Text>
          <Text mono size="sm" variant="muted">
            v{APP_VERSION}
          </Text>
          <Box grow />
          <Button
            render={
              <Text
                as="a"
                href={CHANGELOG_URL}
                rel="noopener noreferrer"
                target="_blank"
              />
            }
            size="sm"
            variant="ghost"
          >
            Full changelog
            <Icon color="muted" name="external-link" size="sm" />
          </Button>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}

function EntryMedia({ media }: { media: NonNullable<Announcement["media"]> }) {
  if (media.kind === "video") {
    return (
      <Box overflow="hidden" rounded="md">
        <Video
          aria-label={media.alt}
          fill="width"
          poster={media.poster}
          src={media.src}
        />
      </Box>
    );
  }
  return (
    <Box surface="sunken" height={180} overflow="hidden" rounded="md">
      <Image alt={media.alt} fill fit="cover" src={media.src} />
    </Box>
  );
}

function AnnouncementRow({
  entry,
  unseen,
  onNavigate,
}: {
  entry: Announcement;
  unseen: boolean;
  onNavigate: (route: string) => void;
}) {
  const badge = entryBadge(entry);
  const RichContent = ANNOUNCEMENT_CONTENT[entry.key];
  return (
    <Box direction="column" gap="xs">
      <Box align="center" direction="row" gap="sm">
        <Badge size="sm" variant={badge.variant}>
          {badge.label}
        </Badge>
        <DiscoveryIndicator placement="inline-right" visible={unseen}>
          <Text size="sm" weight="semibold">
            {entry.title}
          </Text>
        </DiscoveryIndicator>
        <Box grow />
        <Text size="xs" variant="muted">
          {formatDateUtc(announcementDate(entry))}
        </Text>
      </Box>
      <MarkdownContent markdown={entry.body} />
      {entry.media && <EntryMedia media={entry.media} />}
      {RichContent && <RichContent />}
      {entry.cta && (
        <Box direction="row">
          <Button
            onClick={() => onNavigate(entry.cta!.route)}
            size="sm"
          >
            {entry.cta.label}
          </Button>
        </Box>
      )}
    </Box>
  );
}
